// src/pages/Timeline.jsx
//
// The Transaction Timeline shows every contribution and approved expense for
// the selected organization and year. Per the financial-analytics visibility
// matrix it is restricted to treasury, governing_body, senior_accounting_clerk
// and junior_accounting_clerk. Access is gated in two places by design:
//   1. Dashboard.jsx only renders the "View Timeline" button for those roles
//      (routing-level gating).
//   2. This component ALSO checks `userRole` itself (defense-in-depth) and the
//      backend returns 403 for restricted roles, so a hidden button is never
//      the only access boundary.

import React, { useState, useEffect, useRef } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useTranslation } from 'react-i18next';
import { API_BASE } from '../api';
import { setI18nLanguage } from '../i18n/config';
import i18n from '../i18n/config';

const ALLOWED_ROLES = [
  'treasury',
  'governing_body',
  'senior_accounting_clerk',
  'junior_accounting_clerk',
];

const Timeline = ({ ngo, userRole, onBack, onHome, onEditProfile }) => {
  const { user, getAccessTokenSilently, logout } = useAuth0();
  const { t } = useTranslation('timeline');
  const { t: tc } = useTranslation('common');

  const isAuthorized = !userRole || ALLOWED_ROLES.includes(userRole);

  const [timelineData, setTimelineData] = useState([]);
  const [year, setYear] = useState('2026');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // ---- Profile dropdown state ----
  const [profileOpen, setProfileOpen] = useState(false);
  const profileRef = useRef(null);

  // ---- Language menu state ----
  const [langMenuOpen, setLangMenuOpen] = useState(false);
  const langMenuRef = useRef(null);

  useEffect(() => {
    if (!profileOpen) return;
    const handleClickOutside = (e) => {
      if (profileRef.current && !profileRef.current.contains(e.target)) {
        setProfileOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [profileOpen]);

  // Close the language menu on outside click.
  useEffect(() => {
    if (!langMenuOpen) return;
    const handleClickOutside = (e) => {
      if (langMenuRef.current && !langMenuRef.current.contains(e.target)) {
        setLangMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [langMenuOpen]);

  useEffect(() => {
    if (!isAuthorized) return;
    if (!ngo?.id) return;

    const fetchTimeline = async () => {
      setLoading(true);
      setError('');

      try {
        const token = await getAccessTokenSilently({
          authorizationParams: {
            audience: process.env.REACT_APP_AUTH0_AUDIENCE,
          },
        });

        const res = await fetch(
          `${API_BASE}/api/audit-logs/timeline/?org_id=${ngo.id}&year=${year}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (!res.ok) {
          throw new Error(`Server error ${res.status}`);
        }

        const json = await res.json();
        setTimelineData(json);
      } catch (err) {
        console.error(err);
        setError(tc('error'));
      } finally {
        setLoading(false);
      }
    };

    fetchTimeline();
  }, [ngo, year, getAccessTokenSilently, isAuthorized, tc]);

  const handleLogout = () => {
    setProfileOpen(false);
    logout({ logoutParams: { returnTo: window.location.origin } });
  };

  const handleEditProfile = () => {
    setProfileOpen(false);
    if (onEditProfile) onEditProfile();
  };

  return (
    <div className="min-h-screen bg-gray-50">

      {/* Top Bar */}
      <div className="bg-white border-b border-gray-200 px-4 py-4 flex items-center gap-3">

        {onHome && (
          <button onClick={onHome} className="text-blue-600 hover:text-blue-700 flex-shrink-0" aria-label="Home" title="Home">
            <svg width="20" height="20" viewBox="0 0 24 24" className="fill-current">
              <path d="M12 3.2 3 10v10h6v-6h6v6h6V10l-9-6.8Z" />
            </svg>
          </button>
        )}

        <button onClick={onBack} aria-label="Back" className="text-blue-600 hover:text-blue-700">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
            <path d="M19 12H5M5 12l7-7M5 12l7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </button>

        <span className="text-sm font-medium text-gray-900 flex-1">
          {t('title')}
        </span>

        <div className="flex-1" />

        {/* ---- LANGUAGE TOGGLE ---- */}
        <div ref={langMenuRef} className="relative">
          <button
            onClick={() => setLangMenuOpen((open) => !open)}
            aria-label="Language menu"
            aria-expanded={langMenuOpen}
            className="text-xs font-medium text-gray-600 border border-gray-200 rounded-lg px-2.5 py-1.5 hover:bg-gray-50 transition"
          >
            {i18n.language === 'ml' ? 'മലയാളം' : 'EN'}
          </button>
          {langMenuOpen && (
            <div className="absolute right-0 top-10 w-32 bg-white rounded-xl shadow-lg border border-gray-100 p-2 z-50">
              <button
                onClick={() => {
                  const lang = 'en';
                  setI18nLanguage(lang);
                  localStorage.setItem('zbookkeeping_language', lang);
                  setLangMenuOpen(false);
                  // Persist to backend (fire-and-forget)
                  getAccessTokenSilently({
                    authorizationParams: { audience: process.env.REACT_APP_AUTH0_AUDIENCE },
                  }).then(token => {
                    fetch(`${window.location.origin}/api/accounts/profile/update/`, {
                      method: 'PATCH',
                      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                      body: JSON.stringify({ preferred_language: lang }),
                    }).catch(() => {});
                  }).catch(() => {});
                }}
                className={`w-full text-left rounded-lg px-3 py-2 text-sm transition ${
                  i18n.language === 'en' ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                English
              </button>
              <button
                onClick={() => {
                  const lang = 'ml';
                  setI18nLanguage(lang);
                  localStorage.setItem('zbookkeeping_language', lang);
                  setLangMenuOpen(false);
                  // Persist to backend (fire-and-forget)
                  getAccessTokenSilently({
                    authorizationParams: { audience: process.env.REACT_APP_AUTH0_AUDIENCE },
                  }).then(token => {
                    fetch(`${window.location.origin}/api/accounts/profile/update/`, {
                      method: 'PATCH',
                      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                      body: JSON.stringify({ preferred_language: lang }),
                    }).catch(() => {});
                  }).catch(() => {});
                }}
                className={`w-full text-left rounded-lg px-3 py-2 text-sm transition ${
                  i18n.language === 'ml' ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                മലയാളം
              </button>
            </div>
          )}
        </div>

        {/* ---- USER AVATAR + PROFILE DROPDOWN ---- */}
        <div ref={profileRef} className="relative">
          <button
            onClick={() => setProfileOpen((open) => !open)}
            aria-label="Account menu"
            aria-expanded={profileOpen}
            className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center text-white text-xs font-medium"
          >
            {user?.name?.charAt(0).toUpperCase() || 'U'}
          </button>

          {profileOpen && (
            <div className="absolute right-0 top-11 w-64 bg-white rounded-xl shadow-lg border border-gray-100 p-4 z-50">
              <p className="text-sm text-gray-900 truncate">{user?.email}</p>
              <p className="text-sm text-gray-400 truncate mb-4">{user?.email}</p>

              <button
                onClick={handleEditProfile}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded-lg py-2.5 text-sm font-medium transition mb-2"
              >
                {tc('editProfile')}
              </button>
              <button
                onClick={handleLogout}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded-lg py-2.5 text-sm font-medium transition"
              >
                {tc('logout')}
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="max-w-sm mx-auto px-4 py-8">

        <h1 className="text-center text-base font-medium text-blue-600 mb-6 uppercase tracking-wider">
          {t('title')}
        </h1>

        {!isAuthorized && (
          <div className="text-center py-12">
            <p className="text-sm text-red-500 mb-2">{t('accessRestricted')}</p>
            <p className="text-sm text-gray-400">
              {t('accessDeniedMessage')}
            </p>
          </div>
        )}

        {isAuthorized && (
          <>
            {/* Year Selector */}
            <div className="mb-6">
              <select
                value={year}
                onChange={(e) => setYear(e.target.value)}
                className="border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-600 focus:outline-none"
              >
                <option value="2024">2024</option>
                <option value="2025">2025</option>
                <option value="2026">2026</option>
              </select>
            </div>

            {loading && (
              <p className="text-center text-sm text-gray-400">
                {tc('loading')}
              </p>
            )}

            {error && (
              <p className="text-center text-sm text-red-500">
                {error}
              </p>
            )}

            {!loading && !error && timelineData.length === 0 && (
              <p className="text-center text-sm text-gray-400">
                {t('noData', { year })}
              </p>
            )}

            {!loading && !error && timelineData.length > 0 && (
              <div className="relative">

                {/* Vertical line — positioned at 50% of the container so it
                    stays aligned with the dots at every screen width. The
                    dots below live in row wrappers that span the same full
                    width, so their 50% resolves to the same x as this line. */}
                <div className="absolute left-1/2 -translate-x-1/2 top-0 bottom-0 w-0.5 bg-blue-300"></div>

                {timelineData.map((item, index) => {
                  // Alternate sides: first card on the right of the line, next
                  // on the left, and so on — each card stacked below the last.
                  const cardOnRight = index % 2 === 0;

                  const card = (
                    <div className="rounded-2xl bg-white p-4 shadow-md">
                      <p className="text-xs font-medium text-gray-900">
                        {item.date}
                      </p>

                      <p className="text-xs text-gray-400 mt-0.5 mb-3">
                        {item.time}
                      </p>

                      <p className="text-sm text-gray-700">
                        {item.msg}
                      </p>
                    </div>
                  );

                  return (
                    <div key={index} className="relative pb-6">

                      {item.month && (
                        <p className="mb-3 text-center text-xs font-semibold text-gray-500">
                          {item.month}
                        </p>
                      )}

                      {cardOnRight ? (
                        <div className="flex justify-start pl-[calc(50%+20px)]">
                          {card}
                        </div>
                      ) : (
                        <div className="flex justify-end pr-[calc(50%+20px)]">
                          {card}
                        </div>
                      )}

                    </div>
                  );
                })}

              </div>
            )}
          </>
        )}

      </div>
    </div>
  );
};

export default Timeline;